import { useOrganizationSettings, usePpd } from '../api/hooks'
import type { PpdRow } from '../api/types'

function money(value: number | null) {
  return value === null
    ? '—'
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function count(value: number) {
  return value.toLocaleString()
}

/**
 * Cost per patient day for a run, by department and bed type (PPD mode).
 *
 * Grouped by department, because that is the grain cost actually exists at — allocation
 * posts to the cost center. The bed-type rows underneath are a breakdown of the
 * department's figure, not independent measurements, and the layout says so by indenting
 * them under a department line that carries the total.
 */
export function PpdPanel({ runId }: { runId: number | null }) {
  const { data: settings } = useOrganizationSettings()
  const ppdEnabled = settings?.ppdModeEnabled ?? false

  const { data, isLoading } = usePpd(runId, ppdEnabled)

  if (!ppdEnabled || runId === null) {
    return null
  }

  if (isLoading || !data) {
    return (
      <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
        Loading patient days…
      </p>
    )
  }

  if (data.rows.length === 0) {
    return (
      <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
        <div className="px-4 py-2" style={{ background: 'var(--color-panel-alt)' }}>
          <h2 className="col-head">cost per patient day</h2>
        </div>
        <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          No occupancy has been recorded for this period. Enter each department's starting
          occupancy on the census screen and PPD appears here.
        </p>
      </section>
    )
  }

  // Grouped in render rather than by the API: the API returns the grain it computes at,
  // and which of the two grains a screen wants is the screen's business.
  const departments = new Map<number, PpdRow[]>()
  for (const row of data.rows) {
    const rows = departments.get(row.costCenterId) ?? []
    rows.push(row)
    departments.set(row.costCenterId, rows)
  }

  return (
    <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
      <div
        className="flex items-center gap-3 px-4 py-2"
        style={{ background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">cost per patient day</h2>
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Patient days are the sum of the midnight census over every day in {data.periodCode}.
        </span>
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
            <th className="col-head px-4 py-1.5">Department / bed type</th>
            <th className="col-head px-4 py-1.5 text-right">Patient days</th>
            <th className="col-head px-4 py-1.5 text-right">Avg census</th>
            <th className="col-head px-4 py-1.5 text-right">Cost</th>
            <th className="col-head px-4 py-1.5 text-right">PPD</th>
          </tr>
        </thead>
        <tbody>
          {[...departments.values()].map((rows) => {
            // Every group was built by pushing at least one row, so this is never
            // undefined; TypeScript cannot see that through a Map lookup.
            const first = rows[0]!

            return [
              <tr
                key={`d-${first.costCenterId}`}
                className="border-b"
                style={{
                  borderColor: 'var(--color-rule-soft)',
                  background: 'var(--color-panel-alt)',
                }}
              >
                <td className="px-4 py-1.5 text-[12px]" style={{ fontWeight: 600 }}>
                  {first.costCenterCode} — {first.costCenterName}
                </td>
                <td className="ident px-4 py-1.5 text-right">
                  {count(first.departmentPatientDays)}
                </td>
                <td className="ident px-4 py-1.5 text-right" />
                <td className="ident px-4 py-1.5 text-right">{money(first.departmentCost)}</td>
                <td className="ident px-4 py-1.5 text-right" style={{ fontWeight: 600 }}>
                  {money(first.departmentPpd)}
                </td>
              </tr>,

              ...rows.map((row) => (
                <tr
                  key={`b-${row.costCenterId}-${row.bedTypeId}`}
                  className="border-b"
                  style={{ borderColor: 'var(--color-rule-soft)' }}
                >
                  <td
                    className="px-4 py-1.5 text-[12px]"
                    style={{ paddingLeft: '2.5rem', color: 'var(--color-muted)' }}
                  >
                    {row.bedTypeName}
                    {row.relativeWeight !== 1 && (
                      <span className="badge ml-1.5" title="Relative cost of an occupied day.">
                        ×{row.relativeWeight}
                      </span>
                    )}
                  </td>
                  <td className="ident px-4 py-1.5 text-right">{count(row.patientDays)}</td>
                  <td
                    className="ident px-4 py-1.5 text-right"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {row.averageCensus.toFixed(1)}
                  </td>
                  <td className="ident px-4 py-1.5 text-right">{money(row.bedTypeCost)}</td>
                  <td className="ident px-4 py-1.5 text-right">{money(row.bedTypePpd)}</td>
                </tr>
              )),
            ]
          })}
        </tbody>
      </table>

      {/*
          A column of identical PPD figures looks like a bug and is in fact the only honest
          answer available: with every weight at 1, a department's cost divides in exact
          proportion to patient days, so cost-over-days is the same for every bed type in
          it. Saying so — and saying what to do about it — is better than letting somebody
          conclude the report is broken.
      */}
      {data.weightsAreUniform && (
        <p className="px-4 py-1.5 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          Every bed type carries the default weight of 1, so each one reports the same cost
          per patient day as its department. Set relative weights on the bed types — an ICU
          day at ×3, say — to divide each department's cost between them.
        </p>
      )}
    </section>
  )
}
